/**
 * FilterBar - Horizontal filter bar with dropdowns and date inputs.
 *
 * Renders a configurable set of filter controls (select dropdowns and date inputs)
 * in a horizontal row on desktop that stacks vertically on mobile. Each filter is
 * defined by a config object specifying its type, label, and options.
 *
 * The component is fully controlled: the parent owns filter values via the
 * `values` and `onChange` props.
 *
 * @example
 * const filters = [
 *	 { key: 'status', label: 'Status', type: 'select', options: [{ value: 'open', label: 'Open' }] },
 *	 { key: 'date_from', label: 'From', type: 'date' },
 * ];
 * <FilterBar filters={filters} values={filterValues} onChange={setFilterValues} />
 */
import React, { useMemo } from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Option for a select-type filter */
export interface FilterOption {
	/** Value sent to the API as the filter parameter */
	value: string;
	/** Human-readable label shown in the dropdown */
	label: string;
}

/**
 * Configuration for a single filter control in the filter bar.
 *
 * @property key	 - Unique key that maps to the filter values object
 * @property label	 - Display label shown above or as placeholder
 * @property type	 - Control type: 'select' for dropdown, 'date' for date picker
 * @property options - Array of options (only used for 'select' type)
 */
export interface FilterConfig {
	key: string;
	label: string;
	type: 'select' | 'date';
	options?: FilterOption[];
}

/**
 * Props for the FilterBar component.
 *
 * @property filters  - Array of filter configurations defining the controls to render
 * @property values	  - Current filter values keyed by filter config key
 * @property onChange  - Callback fired with the updated values when any filter changes
 */
export interface FilterBarProps {
	filters: FilterConfig[];
	values: Record<string, string>;
	onChange: (values: Record<string, string>) => void;
	/** Optional extra classes appended to the root element. */
	className?: string;
}

const FilterBar: React.FC<FilterBarProps> = ({ filters, values, onChange, className = '' }) => {
	/* Deduplicate filter options by both value and label to prevent duplicate entries.
	 * Two records with different IDs but the same display name look like dupes to the user. */
	const dedupedFilters = useMemo(() => {
		return filters.map((filter) => {
			if (!filter.options) return filter;
			const seenValues = new Set<string>();
			const seenLabels = new Set<string>();
			const uniqueOptions = filter.options.filter((opt) => {
				if (seenValues.has(opt.value)) return false;
				const normalizedLabel = opt.label.toLowerCase().trim();
				if (seenLabels.has(normalizedLabel)) return false;
				seenValues.add(opt.value);
				seenLabels.add(normalizedLabel);
				return true;
			});
			return { ...filter, options: uniqueOptions };
		});
	}, [filters]);

	/**
	 * Handles a change to a single filter value.
	 * Merges the new value into the existing values object and calls onChange.
	 */
	const handleFilterChange = (key: string, value: string) => {
		onChange({ ...values, [key]: value });
	};

	/**
	 * Resets all filters to empty strings.
	 * Called when the user clicks the "Clear" button.
	 */
	const handleClearAll = () => {
		const cleared: Record<string, string> = {};
		filters.forEach((f) => {
			cleared[f.key] = '';
		});
		onChange(cleared);
	};

	/* Check if any filter has a non-empty value to conditionally show the clear button */
	const hasActiveFilters = Object.values(values).some((v) => v !== '');

	return (
		/* Responsive layout: wraps on mobile, horizontal row on desktop */
		<div className={`filter-bar flex flex-wrap items-end gap-12px ${className}`} data-ref="molecule-filter-bar">
			{/* Render each filter control based on its type */}
			{dedupedFilters.map((filter) => (
				<div key={filter.key} className="filter-bar__filter flex flex-col min-w-[140px]">
					{/* Filter label */}
					<label htmlFor={`filter-${filter.key}`} className="text-12px font-sans font-medium text-brand-text-muted mb-4px">
						{filter.label}
					</label>

					{/* Select dropdown for 'select' type filters */}
					{filter.type === 'select' && (
						<select id={`filter-${filter.key}`} value={values[filter.key] || ''} onChange={(e) => handleFilterChange(filter.key, e.target.value)} className="block w-full rounded-md border border-brand-border px-10px py-8px text-16px font-sans min-h-44px bg-white focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary">
							{/* Default empty option showing the filter label as placeholder */}
							<option value="">All {filter.label}</option>
							{filter.options?.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</select>
					)}

					{/* Date input for 'date' type filters */}
					{filter.type === 'date' && (
						<input id={`filter-${filter.key}`} type="date" value={values[filter.key] || ''} onChange={(e) => handleFilterChange(filter.key, e.target.value)} className="block w-full rounded-md border border-brand-border px-10px py-8px text-16px font-sans min-h-44px bg-white focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary" />
					)}
				</div>
			))}

			{/* Clear All button — only shown when at least one filter is active */}
			{hasActiveFilters && (
				<button type="button" onClick={handleClearAll} className="filter-bar__clear inline-flex items-center px-12px py-8px text-14px font-sans font-medium text-brand-text-muted hover:text-brand-primary min-h-44px transition-colors">
					{/* X icon before label */}
					<svg className="h-16px w-16px mr-4px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
						<path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
					</svg>
					Clear
				</button>
			)}
		</div>
	);
};

export default FilterBar;
